import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, Check, ChevronLeft, Fuel, MessageCircle, Gift, Zap } from "lucide-react";
import { toast } from "sonner";
import { lookupVehicle, toErrorMessage } from "../../services/crmApi";
import {
  fetchServicePackages,
  BODY_CLASS_TO_API,
  FUEL_TO_API,
} from "../../services/publicCrmApi";
import { trackEvent, trackConversion } from "../../utils/gtag";
import {
  buildFallbackCatalog,
  buildFallbackPrice,
} from "../../data/carServiceFallback";
import { buildCarServiceWhatsAppHref } from "./whatsappConfig";
import { useCarServiceCity } from "./CarServiceCityContext";

/**
 * Resolves a quoted price for a vehicle:
 *   1. Calls CRM /service-packages with body_class + fuel_type filters
 *      (the server returns a `quoted_price` on the GENERAL package).
 *   2. Falls back to bundled tier-based heuristic if API is down/missing.
 * Returns the same shape the rest of the LP expects.
 */
async function resolveQuotedPrice({ body_class, fuel, brand }) {
  const bodyApi = BODY_CLASS_TO_API[body_class] || undefined;
  const fuelApi = FUEL_TO_API[fuel] || undefined;
  try {
    const data = await fetchServicePackages({
      body_class: bodyApi,
      fuel_type: fuelApi,
      brand: brand || undefined,
    });
    const general =
      data?.packages?.find((p) => p.category === "GENERAL" && p.is_active !== false) ||
      data?.packages?.[0];
    if (general?.quoted_price != null) {
      return {
        price_id: general.id,
        package_id: general.id,
        amount_display: general.quoted_price,
        amount: general.quoted_price * 100,
        currency: general.currency || "INR",
        tax_inclusive: true,
        duration_minutes: 90,
        warranty: "1,000 km / 30 days",
        inclusions: (general.items || []).map((it) => ({
          icon: "check",
          label: it.name,
        })),
        exclusions: ["Brake pad replacement (if needed)", "Battery (if dead)"],
        city_serviceable: true,
        source: "crm_live",
      };
    }
  } catch (_) {
    /* fall through to bundled fallback */
  }
  return buildFallbackPrice({ body_class, fuel, brand });
}

/**
 * Dual-mode pricing lookup widget — the heart of the LP.
 *
 *   Tab A: By Car Number → CRM Surepass /vehicles/lookup
 *   Tab B: By Make & Model → visual 3-step flow (brand → model → fuel)
 *            • Uses live CRM /services/catalog if available
 *            • Falls back to bundled catalog (carServiceFallback.js) on error
 *              or while CRM is loading — LP works instantly even on day 1
 *              when CRM endpoints aren't built yet.
 *            • Pricing falls back to tier-based heuristic if /services/pricing
 *              is unavailable, with `source: 'bundled_fallback'` in payload
 *              so analytics/ops can see which price originated where.
 */
const CarServiceLookupWidget = ({ onPriceResolved, initialPrefill }) => {
  const { selectedCity } = useCarServiceCity();
  const whatsappSalesHref = buildCarServiceWhatsAppHref({
    city: selectedCity,
    clickType: "lookup_widget",
  });
  const [tab, setTab] = useState("number"); // 'number' | 'make_model'

  // -- By Car Number state ---------------------------------------------
  const [regNo, setRegNo] = useState("");
  const [regLoading, setRegLoading] = useState(false);

  // -- By Make/Model state ----------------------------------------------
  // Catalog is ALWAYS populated (fallback on mount, live replaces later).
  const [catalog, setCatalog] = useState(() => buildFallbackCatalog());
  const [catalogSource, setCatalogSource] = useState("bundled_fallback");
  const [mmStep, setMmStep] = useState("brand"); // 'brand' | 'model' | 'fuel'
  const [brandId, setBrandId] = useState("");
  const [modelId, setModelId] = useState("");
  const [fuel, setFuel] = useState("");
  const [mmLoading, setMmLoading] = useState(false);

  // Deep-link prefill — consume once on mount when arriving from a programmatic
  // SEO page. URL drives initial tab + brand/model selection so the user never
  // has to re-pick what they already clicked through.
  useEffect(() => {
    if (!initialPrefill) return;
    const { brand, model } = initialPrefill;
    if (!brand) return;
    const cat = buildFallbackCatalog();
    const matchedBrand = cat.brands.find(
      (b) => b.id === brand || b.id === brand.toLowerCase().replace(/\s+/g, "-")
    );
    if (!matchedBrand) return;
    setTab("make_model");
    setBrandId(matchedBrand.id);
    if (model) {
      const matchedModel = matchedBrand.models?.find(
        (m) => m.id === model || m.id === model.toLowerCase().replace(/\s+/g, "-")
      );
      if (matchedModel) {
        setModelId(matchedModel.id);
        setMmStep("fuel");
        return;
      }
    }
    setMmStep("model");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedBrand = useMemo(
    () => catalog?.brands?.find((b) => b.id === brandId) || null,
    [catalog, brandId],
  );
  const selectedModel = useMemo(
    () => selectedBrand?.models?.find((m) => m.id === modelId) || null,
    [selectedBrand, modelId],
  );

  // Attempt to upgrade fallback catalog to live CRM catalog once, silently.
  // If it fails, we stay on the fallback — the tab still works.
  // Catalog upgrade currently disabled — the new public CRM doesn't expose
  // a /service-catalog tree (it ships a /service-packages list instead).
  // The bundled brand/model catalog stays authoritative for the visual
  // make/model picker; CRM-side pricing kicks in once a fuel is chosen via
  // /service-packages?body_class=…&fuel_type=…
  const upgradeToLiveCatalog = useCallback(async () => {}, []);

  useEffect(() => {
    if (tab === "make_model" && catalogSource === "bundled_fallback") {
      upgradeToLiveCatalog();
    }
  }, [tab, catalogSource, upgradeToLiveCatalog]);

  const resetMm = () => {
    setMmStep("brand"); setBrandId(""); setModelId(""); setFuel("");
  };
  const goBackMm = () => {
    if (mmStep === "fuel") { setFuel(""); setMmStep("model"); return; }
    if (mmStep === "model") { setModelId(""); setMmStep("brand"); return; }
  };

  // ---- By-Number submit -----------------------------------------------
  async function handleNumberSubmit(e) {
    e?.preventDefault?.();
    const cleaned = regNo.replace(/\s+/g, "").toUpperCase();
    if (cleaned.length < 6) {
      toast.error("Enter a valid car number (e.g., KA01AB1234)");
      return;
    }
    setRegLoading(true);
    trackEvent("lookup_attempt", { method: "registration_number" });
    try {
      const result = await lookupVehicle({ reg_no: cleaned, country_code: "IN" });
      if (!result?.success || !result?.vehicle) throw new Error("vehicle_not_found");
      const v = result.vehicle;
      const brandId = (v.brand || v.maker || "").toLowerCase().replace(/\s+/g, "-");
      const modelId = (v.model || "").toLowerCase().replace(/\s+/g, "-");
      const fuel = (v.fuel_type || v.fuel || "petrol").toLowerCase();
      const body_class = findBodyClass(catalog, brandId, modelId) || "hatchback";

      const price = await resolveQuotedPrice({
        body_class,
        fuel,
        brand: v.brand || v.maker,
      });

      trackEvent("pricing_revealed", { method: "registration_number", source: price.source || "crm_live" });
      trackConversion("pricing_revealed", { value: price.amount_display });
      onPriceResolved?.(price, {
        ...v,
        brand_id: brandId,
        model_id: modelId,
        body_class,
        fuel,
        registration_number: cleaned,
        display: `${v.brand || v.maker || ""} ${v.model || ""} ${v.variant || ""}`.trim(),
      });
    } catch (e) {
      toast.error("We couldn't find your car. Try selecting it manually.");
      setTab("make_model");
    } finally {
      setRegLoading(false);
    }
  }

  // ---- By-Make/Model submit -------------------------------------------
  async function completeMakeModelFlow(finalFuel) {
    setMmLoading(true);
    trackEvent("lookup_attempt", { method: "make_model" });
    try {
      const body_class = selectedModel?.body_class || "hatchback";
      const price = await resolveQuotedPrice({
        body_class,
        fuel: finalFuel,
        brand: selectedBrand?.name,
      });

      trackEvent("pricing_revealed", { method: "make_model", source: price.source || "crm_live" });
      trackConversion("pricing_revealed", { value: price.amount_display });
      onPriceResolved?.(price, {
        brand_id: brandId,
        model_id: modelId,
        brand: selectedBrand?.name,
        model: selectedModel?.name,
        body_class,
        fuel: finalFuel,
        display: `${selectedBrand?.name || ""} ${selectedModel?.name || ""}`.trim(),
      });
    } catch (e) {
      toast.error(toErrorMessage(e, "Couldn't fetch price. Try again or WhatsApp us."));
    } finally {
      setMmLoading(false);
    }
  }

  // ---- UI ---------------------------------------------------------------
  return (
    <motion.div
      initial={{ y: 12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white border border-slate-200 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden"
      data-testid="lookup-widget"
    >
      {/* Pill toggle tabs — primary self-serve entry path */}
      <div className="p-2 bg-slate-100 border-b border-slate-100" role="tablist">
        <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-xl p-1">
          <PillTab active={tab === "number"} onClick={() => setTab("number")} testid="tab-by-number">
            By Car Number
          </PillTab>
          <PillTab active={tab === "make_model"} onClick={() => setTab("make_model")} testid="tab-by-make-model">
            By Make &amp; Model
          </PillTab>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <AnimatePresence mode="wait">
          {tab === "number" ? (
            <motion.form
              key="form-number"
              onSubmit={handleNumberSubmit}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              <label className="block">
                <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">
                  Car Number
                </span>
                <input
                  type="text" value={regNo} onChange={(e) => setRegNo(e.target.value.toUpperCase())}
                  placeholder="KA01AB1234" data-testid="reg-number-input"
                  autoComplete="off" spellCheck="false" maxLength={12}
                  className="w-full px-4 py-3.5 font-mono font-bold text-lg tracking-wider uppercase bg-slate-50 border border-slate-200 rounded-md outline-none transition-all focus:bg-white focus:border-slate-900 focus:ring-2 focus:ring-blue-600/20 placeholder:text-slate-300 placeholder:font-normal placeholder:tracking-normal"
                />
              </label>
              <SubmitBtn loading={regLoading} icon={Search} testid="lookup-submit-button">
                Get My Service Price
              </SubmitBtn>
            </motion.form>
          ) : (
            <motion.div
              key="mm-flow"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              {/* Stepper header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {mmStep !== "brand" && (
                    <button
                      type="button" onClick={goBackMm}
                      data-testid="mm-back-btn"
                      className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" /> BACK
                    </button>
                  )}
                </div>
                <Breadcrumb step={mmStep} brand={selectedBrand?.name} model={selectedModel?.name} />
              </div>

              <AnimatePresence mode="wait">
                {mmStep === "brand" && (
                  <BrandGrid
                    key="brand-grid"
                    brands={catalog.brands}
                    onPick={(id) => { setBrandId(id); setMmStep("model"); trackEvent("mm_brand_selected", { brand_id: id }); }}
                  />
                )}

                {mmStep === "model" && selectedBrand && (
                  <ModelGrid
                    key="model-grid"
                    brand={selectedBrand}
                    onPick={(id) => { setModelId(id); setMmStep("fuel"); trackEvent("mm_model_selected", { model_id: id }); }}
                  />
                )}

                {mmStep === "fuel" && selectedModel && (
                  <FuelStep
                    key="fuel-step"
                    model={selectedModel}
                    selectedFuel={fuel}
                    loading={mmLoading}
                    onPickFuel={(f) => setFuel(f)}
                    onConfirm={() => {
                      if (!fuel) return;
                      trackEvent("mm_fuel_selected", { fuel });
                      completeMakeModelFlow(fuel);
                    }}
                    onReset={resetMm}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {tab === "number" && (
          <p className="mt-3 text-[11px] text-center text-slate-500">
            Powered by VAHAN · Your data stays private
          </p>
        )}
      </div>

      {/* Soft WhatsApp invitation — sits below the form as an inviting "easier
          way" alternative. Discount + 2-min-reply act as gentle attractors,
          not pressure tactics. Visually distinct (warm green tint) so it
          reads as a separate path, not a duplicate of the blue button. */}
      <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
            Or, easier way
          </span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <a
          href={whatsappSalesHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("whatsapp_clicked", { location: "lookup_widget_below_form" });
          }}
          data-testid="lookup-whatsapp-cta"
          className="relative w-full block rounded-xl border-2 border-green-600/80 hover:border-green-600 hover:-translate-y-[1px] active:translate-y-0 transition-all overflow-hidden group shadow-[0_4px_14px_-4px_rgba(22,163,74,0.25)] hover:shadow-[0_10px_24px_-6px_rgba(22,163,74,0.4)]"
        >
          {/* Top deal strip — full-width, animated shimmer for attention.
              Lives above the content row so it never overlaps text on mobile. */}
          <div
            data-testid="whatsapp-discount-chip"
            className="relative flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-400 text-amber-950 text-[10.5px] sm:text-[11px] font-extrabold uppercase tracking-[0.08em] overflow-hidden"
          >
            <Gift className="h-3 w-3 shrink-0" strokeWidth={2.6} />
            <span>Save ₹500 extra · WhatsApp only</span>
            {/* Shimmer overlay */}
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer" />
          </div>

          {/* Content row */}
          <div className="flex items-center gap-3 px-4 py-3.5 bg-gradient-to-br from-green-50 to-white group-hover:from-green-50 group-hover:to-green-50/40 transition-colors">
            <span className="relative shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-green-600 group-hover:bg-green-700 transition-colors shadow-[0_4px_12px_-2px_rgba(22,163,74,0.5)]">
              <MessageCircle className="h-5 w-5 text-white" strokeWidth={2.4} />
              {/* Live online pulse dot */}
              <span className="absolute top-0 right-0 flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
              </span>
            </span>
            <span className="flex flex-col items-start leading-tight min-w-0">
              <span className="text-[14px] sm:text-[15px] font-extrabold text-green-800 tracking-tight">
                Chat with us on WhatsApp
              </span>
              <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] sm:text-[11.5px] font-semibold text-green-700">
                <Zap className="h-3 w-3 fill-green-600 text-green-600 shrink-0" strokeWidth={2.5} />
                <span>Reply within 2 mins · Instant quote</span>
              </span>
            </span>
          </div>
        </a>
      </div>
    </motion.div>
  );
};

// --- Sub-components ----------------------------------------------------

const PillTab = ({ active, onClick, testid, children }) => (
  <button
    role="tab" aria-selected={active} onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 text-xs sm:text-sm font-bold tracking-tight rounded-lg transition-all ${
      active
        ? "bg-white text-slate-900 shadow-sm"
        : "text-slate-500 hover:text-slate-700"
    }`}
  >
    {children}
  </button>
);

const TabBtn = ({ active, onClick, testid, children }) => (
  <button
    role="tab" aria-selected={active} onClick={onClick} data-testid={testid}
    className={`flex-1 px-5 py-4 text-xs sm:text-sm font-bold tracking-wide transition-colors relative ${
      active ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
    }`}
  >
    {children}
    {active && (
      <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-600" />
    )}
  </button>
);

const SubmitBtn = ({ loading, disabled, icon: Icon, testid, ctaLabel, ctaLocation = "hero", children }) => (
  <button
    type="submit" disabled={loading || disabled} data-testid={testid}
    data-cta-label={ctaLabel || (typeof children === "string" ? children : "Get Quote")}
    data-cta-location={ctaLocation}
    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-white font-extrabold text-base tracking-tight shadow-[0_8px_24px_-6px_rgba(37,99,235,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(37,99,235,0.6)] hover:-translate-y-[2px]"
  >
    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : Icon ? <Icon className="h-5 w-5" /> : null}
    {loading ? "Fetching…" : children}
  </button>
);

const Breadcrumb = ({ step, brand, model }) => (
  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
    <span className={step === "brand" ? "text-blue-600" : brand ? "text-slate-700" : ""}>Brand</span>
    <span>·</span>
    <span className={step === "model" ? "text-blue-600" : model ? "text-slate-700" : ""}>Model</span>
    <span>·</span>
    <span className={step === "fuel" ? "text-blue-600" : ""}>Fuel</span>
  </div>
);

const BrandGrid = ({ brands, onPick }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    className="grid grid-cols-3 sm:grid-cols-4 gap-2"
    data-testid="mm-brand-grid"
  >
    {brands.map((b) => (
      <BrandCard key={b.id} brand={b} onClick={() => onPick(b.id)} />
    ))}
  </motion.div>
);

const BrandCard = ({ brand, onClick }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const initials = brand.name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
  const showLogo = brand.logo && !logoFailed;

  return (
    <button
      onClick={onClick}
      title={brand.name}
      aria-label={brand.name}
      data-testid={`brand-${brand.id}`}
      className="group aspect-[5/4] flex items-center justify-center bg-white border border-slate-200 rounded-md p-3 hover:border-slate-900 hover:bg-slate-50 hover:-translate-y-[2px] hover:shadow-md active:scale-[0.97] transition-all"
    >
      {showLogo ? (
        <img
          src={brand.logo}
          alt={`${brand.name} logo`}
          loading="lazy"
          onError={() => setLogoFailed(true)}
          className="max-h-12 max-w-full object-contain group-hover:scale-105 transition-transform"
          data-testid={`brand-logo-${brand.id}`}
        />
      ) : (
        <span className="flex flex-col items-center justify-center gap-1.5">
          <span
            className="w-10 h-10 rounded-full inline-flex items-center justify-center font-bold text-white text-xs tracking-tight"
            style={{ backgroundColor: brand.color }}
          >
            {initials}
          </span>
          <span className="text-[11px] font-bold text-slate-900 leading-tight text-center">
            {brand.name}
          </span>
        </span>
      )}
    </button>
  );
};

const ModelGrid = ({ brand, onPick }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
    transition={{ duration: 0.2 }}
    className="grid grid-cols-2 sm:grid-cols-3 gap-2"
    data-testid="mm-model-grid"
  >
    {brand.models.map((m) => (
      <button
        key={m.id}
        onClick={() => onPick(m.id)}
        data-testid={`model-${m.id}`}
        className="p-3 bg-white border border-slate-200 rounded-md hover:border-slate-900 hover:bg-slate-50 hover:-translate-y-[1px] hover:shadow-sm active:scale-[0.98] transition-all text-left"
      >
        <div className="text-sm font-bold text-slate-900 leading-tight">{m.name}</div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mt-1">
          {m.body_class.replace("_", " ")}
        </div>
      </button>
    ))}
  </motion.div>
);

const FuelStep = ({ model, selectedFuel, loading, onPickFuel, onConfirm, onReset }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
    transition={{ duration: 0.2 }}
    className="space-y-4"
    data-testid="mm-fuel-step"
  >
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
      <Fuel className="h-3.5 w-3.5 text-blue-600" /> Fuel Type
    </div>
    <div className="grid grid-cols-3 gap-2">
      {model.fuels.map((f) => (
        <button
          key={f}
          onClick={() => onPickFuel(f)}
          data-testid={`fuel-${f}`}
          className={`py-3 rounded-md border-2 text-sm font-bold uppercase tracking-wide transition-all ${
            selectedFuel === f
              ? "border-blue-600 bg-blue-50 text-blue-700"
              : "border-slate-200 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"
          }`}
        >
          {f}
        </button>
      ))}
    </div>

    <button
      type="button"
      onClick={onConfirm}
      disabled={loading || !selectedFuel}
      data-testid="mm-submit-button"
      data-cta-label="Get My Service Price"
      data-cta-location="hero"
      className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-white font-extrabold text-base tracking-tight shadow-[0_8px_24px_-6px_rgba(37,99,235,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(37,99,235,0.6)] hover:-translate-y-[2px]"
    >
      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
      {loading ? "Fetching…" : "Get My Service Price"}
    </button>
    <button
      type="button"
      onClick={onReset}
      className="w-full text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors py-2"
    >
      Start over
    </button>
  </motion.div>
);

// --- Helpers ----------------------------------------------------------

function findBodyClass(catalog, brandId, modelId) {
  const brand = catalog?.brands?.find((b) => b.id === brandId);
  const model = brand?.models?.find((m) => m.id === modelId);
  return model?.body_class;
}

export default CarServiceLookupWidget;
